import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Switch} from '@/components/ui/switch'
import {SectionCard} from '@/components/section-card'
import {MunicipioPicker} from '@/components/municipio-picker'
import {
  EstadoDato,
  Procedencia,
  sinConfirmar,
  TrazabilidadSwitch,
  useTrazabilidad,
} from '@/components/perfil/provenance-note'
import {
  useMunicipio,
  useProfileFacts,
  useUpdateProfileFact,
  type Municipio,
} from '@/hooks/salud/perfil_riesgo/use-perfil'
import {translateApiError} from '@/lib/errors'

function MunicipioFactRow({
  businessId,
  canEdit,
  desplegado,
}: {
  businessId: string
  canEdit: boolean
  desplegado: boolean
}) {
  const {t} = useTranslation()
  const {data: facts} = useProfileFacts(businessId)
  const fact = facts?.find((f) => f.fact_key === 'divipola')
  const code = typeof fact?.value === 'string' ? fact.value : null
  const {data: resolved} = useMunicipio(businessId, code)
  const update = useUpdateProfileFact(businessId)
  const [error, setError] = useState<string | null>(null)

  function pick(municipio: Municipio) {
    setError(null)
    update.mutate({fact_key: 'divipola', value: municipio.code}, {onError: (err) => setError(translateApiError(err))})
  }

  const label = fact?.prompt_es ?? t('perfilRiesgo.facts.municipioLabel')

  return (
    <div className="space-y-2" data-testid="fact-divipola">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-medium text-foreground">{label}</p>
        {/* `testId` propio: con el default `estado-dato` las dos filas repiten el `data-testid`
            (strict mode de Playwright). */}
        {sinConfirmar(fact?.confirmed_by ?? null, fact?.confirmed_at ?? null) && (
          <EstadoDato estado="pendiente" testId="estado-divipola" />
        )}
      </div>
      <MunicipioPicker businessId={businessId} value={resolved ?? null} disabled={!canEdit} onPick={pick} />
      <Procedencia
        confirmedBy={fact?.confirmed_by ?? null}
        confirmedAt={fact?.confirmed_at ?? null}
        desplegado={desplegado}
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

function ContratistasFactRow({
  businessId,
  canEdit,
  desplegado,
}: {
  businessId: string
  canEdit: boolean
  desplegado: boolean
}) {
  const {t} = useTranslation()
  const {data: facts} = useProfileFacts(businessId)
  const fact = facts?.find((f) => f.fact_key === 'usa_contratistas_est')
  const update = useUpdateProfileFact(businessId)
  const [error, setError] = useState<string | null>(null)
  const checked = fact?.value === true

  function toggle(next: boolean) {
    setError(null)
    update.mutate({fact_key: 'usa_contratistas_est', value: next}, {onError: (err) => setError(translateApiError(err))})
  }

  const label = fact?.prompt_es ?? t('perfilRiesgo.facts.contratistasLabel')

  return (
    <div className="surface-divider space-y-2 border-t pt-4" data-testid="fact-usa-contratistas-est">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium text-foreground">{label}</p>
          {sinConfirmar(fact?.confirmed_by ?? null, fact?.confirmed_at ?? null) && (
            <EstadoDato estado="pendiente" testId="estado-usa-contratistas-est" />
          )}
        </div>
        <Switch checked={checked} disabled={!canEdit || update.isPending} onCheckedChange={toggle} aria-label={label} />
      </div>
      <Procedencia
        confirmedBy={fact?.confirmed_by ?? null}
        confirmedAt={fact?.confirmed_at ?? null}
        desplegado={desplegado}
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

/** onboarding-spec §5.1, §7. `sustancias_especificas` renders in the hazards section, under `quimicos`. */
export function FactsSection({businessId, canEdit}: {businessId: string; canEdit: boolean}) {
  const {t} = useTranslation()
  const [desplegado, setDesplegado] = useTrazabilidad()
  return (
    <SectionCard
      testId="perfil-facts-section"
      title={t('perfilRiesgo.facts.title')}
      actions={<TrazabilidadSwitch desplegado={desplegado} onChange={setDesplegado} testId="facts-trazabilidad" />}
      bodyClassName="space-y-4 p-5">
      <MunicipioFactRow businessId={businessId} canEdit={canEdit} desplegado={desplegado} />
      <ContratistasFactRow businessId={businessId} canEdit={canEdit} desplegado={desplegado} />
    </SectionCard>
  )
}
